'use client'

import { useCallback, useMemo } from 'react'

import { TIME_RANGE_CUSTOM_VALUE } from '../../const'
import { useGatewayTimeRangeFilterUiConfig } from '../../hooks/useGatewayTimeRangeFilterUiConfig'
import { useServiceMapFiltersStore } from '../../stores/service-map-filters.store'
import type { ServiceMapTimeRangeFilterValue } from '../../types/serviceMapTimeRangeFilterValue.type'
import { formatCustomRangeLabel, ServiceMapTimeRangeFilter, toCustomRange } from '../TimeRangeFilter'
import type {
  ServiceMapCustomRangeApplied,
  ServiceMapTimeRangeFilterLabels,
  ServiceMapTimeRangeFilterOption,
} from '../TimeRangeFilter/time-range.types'

type GatewayTimeRangeFilterProps = {
  labels?: Partial<ServiceMapTimeRangeFilterLabels>
  options?: readonly ServiceMapTimeRangeFilterOption[]
}

export function GatewayTimeRangeFilter({ labels, options }: GatewayTimeRangeFilterProps) {
  const { options: resolvedOptions, labels: resolvedLabels } = useGatewayTimeRangeFilterUiConfig(labels, options)
  const timeRangeValue = useServiceMapFiltersStore((state) => state.timeRangeValue)
  const customRangeApplied = useServiceMapFiltersStore((state) => state.customRangeApplied)
  const setTimeRangeFilter = useServiceMapFiltersStore((state) => state.setTimeRangeFilter)

  const timeRangeCustomRange = useMemo(() => {
    if (!customRangeApplied) {
      return undefined
    }

    return toCustomRange({
      startAt: new Date(customRangeApplied.startAt),
      endAt: new Date(customRangeApplied.endAt),
    })
  }, [customRangeApplied])

  const timeRangeCustomValue =
    timeRangeValue === TIME_RANGE_CUSTOM_VALUE ? formatCustomRangeLabel(timeRangeCustomRange) : undefined

  const handleTimeRangeApply = useCallback(
    (value: ServiceMapTimeRangeFilterValue, applied?: ServiceMapCustomRangeApplied | null) => {
      setTimeRangeFilter(
        value,
        value === TIME_RANGE_CUSTOM_VALUE && applied
          ? { startAt: applied.startAt.getTime(), endAt: applied.endAt.getTime() }
          : null
      )
    },
    [setTimeRangeFilter]
  )

  return (
    <ServiceMapTimeRangeFilter
      value={timeRangeValue}
      customValue={timeRangeCustomValue}
      customRange={timeRangeCustomRange}
      options={[...resolvedOptions]}
      labels={resolvedLabels}
      onApply={handleTimeRangeApply}
    />
  )
}
